<template>
  <div class="text-white">
    <!-- Header -->
    <div class="mb-4 text-lg font-bold">Share {{ displayTitle }}</div>

    <!-- Context card for non-review types -->
    <div v-if="profile" class="mb-4">
      <div class="flex items-center p-4 space-x-4 bg-gray-800 rounded-lg">
        <div class="flex-shrink-0 w-12 h-12">
          <UserAvatar :user="profile" size="12" />
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="text-base font-bold truncate">
            {{ profile.display_name || profile.username }}
          </h3>
          <p class="text-gray-400">@{{ profile.username }}</p>

          <div
            class="flex flex-row gap-2 mt-1 font-medium text-center select-none text-2xs">
            <span
              class="text-white/80 after:content-['•'] after:ml-2 last:after:content-['']">
              Member since {{ memberSinceDate }}
            </span>
            <span class="text-white/80">
              {{ reviewCount }} {{ reviewCount === 1 ? 'review' : 'reviews' }}
            </span>
          </div>

          <p v-if="profile.bio" class="mt-2 text-sm text-gray-300 line-clamp-2">
            {{ profile.bio }}
          </p>
        </div>
      </div>
    </div>

    <div v-else-if="list" class="mb-4">
      <!-- Horizontal poster scroll -->
      <div
        v-if="listItemPosters.length > 0"
        class="flex pb-2 -mx-4 px-4 space-x-2 overflow-x-auto scrollbar-hide">
        <div
          v-for="item in listItemPosters"
          :key="item.id"
          class="w-20 shrink-0 overflow-hidden rounded-md shadow-sm">
          <img
            :src="`https://image.tmdb.org/t/p/w185${item.poster}`"
            :alt="item.name"
            class="object-cover w-full aspect-[2/3] bg-gray-800" />
        </div>
      </div>

      <!-- Meta info -->
      <div class="flex items-center mt-2 text-2xs text-white/60">
        <span class="mr-3">
          <fa-icon :icon="['far', 'rectangle-list']" class="mr-1" />
          {{ list.list_items?.[0]?.count || 0 }} items
        </span>
        <span v-if="list.is_private" class="text-red-400">
          <fa-icon :icon="['fas', 'lock']" class="mr-1" /> Private list
        </span>
        <span v-else class="text-green-400">
          <fa-icon :icon="['far', 'globe']" class="mr-1" /> Public list
        </span>
      </div>

      <!-- Private list warning message -->
      <div
        v-if="privateList && !madePublic"
        class="p-3 mt-3 border rounded-md bg-red-500/20 border-red-500/30">
        <p class="flex items-center text-sm text-white">
          <fa-icon :icon="['fas', 'lock']" class="mr-2 text-red-400" />
          This list is private and cannot be shared.
        </p>
        <p class="mt-1 text-xs text-gray-300">
          Make it public so others can see it.
        </p>
      </div>
    </div>

    <div v-else-if="!review" class="mb-4">
      <div
        v-if="title"
        class="border border-gray-700 rounded-lg overflow-hidden">
        <TitleStrip
          :title="normalizedTitle"
          :title-type="title.media_type || title.tmdb_type || 'movie'"
          :condensed="true"
          :dark="true" />
      </div>
      <div v-else class="p-4 bg-gray-800 rounded-lg">
        <h3 class="text-xl font-bold">Spectra</h3>
        <p class="text-gray-400">Share this page</p>
      </div>
    </div>

    <!-- Show sharing options only if not a private list (or made public) -->
    <template v-if="!privateList || madePublic">
      <!-- SECTION 1: Review Card (hero) -->
      <div v-if="review" class="space-y-4">
        <ShareImageDownloads
          :review="review"
          :title="title"
          :image-blobs="imageBlobs"
          :show-label="false" />
        <p class="text-center text-xs text-gray-500">
          For Instagram, X, Stories and more
        </p>
      </div>

      <!-- SECTION 2: Share Link -->
      <div
        :class="[
          review || list
            ? 'mt-4 pt-4 border-t border-white/10'
            : 'mt-0'
        ]">
        <p
          v-if="review || list"
          class="mb-3 text-xs font-semibold tracking-wide text-gray-400 uppercase">
          Share Link
        </p>

        <!-- Mobile: single native share button covers all link sharing -->
        <button
          v-if="isNativePlatform"
          class="group relative w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 overflow-hidden bg-gray-800 hover:bg-gray-700 text-white active:scale-[0.98]"
          @click="handleNativeShare">
          <div class="flex relative z-10 gap-2 items-center">
            <fa-icon
              icon="arrow-up-from-bracket"
              class="text-base" />
            <span>Share Link</span>
          </div>
        </button>

        <!-- Desktop: Copy Link + WhatsApp -->
        <ShareLinkButtons
          v-if="!isNativePlatform"
          :whatsapp-label="'WhatsApp'"
          :show-label="false" />
      </div>
    </template>

    <!-- For private lists, show make public + cancel -->
    <template v-else>
      <div class="pt-4 mt-4 border-t border-white/10">
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            @click="closeModal">
            Cancel
          </button>
          <button
            type="button"
            :disabled="isMakingPublic"
            class="flex-[2] flex items-center justify-center gap-2 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="makePublicAndShare">
            <template v-if="isMakingPublic">
              Making public...
              <fa-icon icon="spinner-third" class="fa-spin" />
            </template>
            <template v-else>
              <fa-icon :icon="['far', 'globe']" />
              Make Public and Share
            </template>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import TitleStrip from '@/components/TitleStrip'
import UserAvatar from '@/components/UserAvatar.vue'
import ShareImageDownloads from '@/components/Share/ShareImageDownloads.vue'
import ShareLinkButtons from '@/components/Share/ShareLinkButtons.vue'
import { useStoreLists } from '@/stores/storeLists'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import { format } from 'date-fns'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'

const storeLists = useStoreLists()
const haptics = useHapticFeedback()

// Check if running on native platform
const isNativePlatform = computed(() => Capacitor.isNativePlatform())

const props = defineProps({
  title: [Object],
  review: [Object],
  profile: [Object],
  list: [Object],
  imageBlobs: {
    type: Object,
    default: null
  },
  privateList: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

// List item posters for horizontal scroll
const listItemPosters = computed(() => {
  if (!props.list?.id) return []
  return storeLists
    .getListItemsByListId(props.list.id)
    .filter((item) => item.titles?.tmdb_poster_path)
    .slice(0, 10)
    .map((item) => ({
      id: item.id,
      poster: item.titles.tmdb_poster_path,
      name: item.titles?.title || item.titles?.name || ''
    }))
})

// Close modal with haptic feedback
const closeModal = () => {
  haptics.light()
  emit('close')
}

// Make list public and show share options
const isMakingPublic = ref(false)
const madePublic = ref(false)

const makePublicAndShare = async () => {
  if (isMakingPublic.value || !props.list?.id) return
  haptics.medium()
  isMakingPublic.value = true
  try {
    const result = await storeLists.editList(props.list.id, {
      is_private: false
    })
    if (result.error) {
      haptics.error()
      isMakingPublic.value = false
      return
    }
    haptics.success()
    madePublic.value = true
  } catch {
    haptics.error()
  }
  isMakingPublic.value = false
}

// Handle native share (iOS/Android)
const handleNativeShare = async () => {
  haptics.medium()

  try {
    // Construct the share URL
    const shareUrl = `https://spectra.reviews${window.location.pathname}${window.location.search || ''}`

    await Share.share({
      text: shareUrl
    })
  } catch (error) {
    // User cancelled or error occurred
    console.error('Error sharing:', error)
  }
}

// Member since date formatted as month and year (e.g., "March 2023")
const memberSinceDate = computed(() => {
  if (!props.profile || !props.profile.created_at) return 'N/A'
  try {
    return format(new Date(props.profile.created_at), 'MMMM yyyy')
  } catch (e) {
    return 'N/A'
  }
})

// Review count from the profile data
const reviewCount = computed(() => {
  if (!props.profile) return 0
  // Check if reviews array exists and has items with count
  if (props.profile.reviews && props.profile.reviews.length > 0) {
    return props.profile.reviews[0].count || 0
  }
  // Fallback for different data structure or no reviews
  return props.profile.review_count || 0
})

// Normalize title object to ensure it has the expected properties
const normalizedTitle = computed(() => {
  if (!props.title) return null

  return {
    ...props.title,
    // Ensure we have a 'title' or 'name' property for TitleStrip
    title:
      props.title.title ||
      props.title.original_title ||
      props.title.name ||
      props.title.original_name,
    name:
      props.title.name ||
      props.title.original_name ||
      props.title.title ||
      props.title.original_title,
    // Ensure we have media_type
    media_type: props.title.media_type || props.title.tmdb_type || 'movie'
  }
})

const displayTitle = computed(() => {
  let titleName = ''
  let userName = ''

  if (props.review) {
    if (props.review?.profiles?.username) {
      userName = props.review.profiles.username
    }

    if (props.review?.title_title) {
      titleName = props.review.title_title
    }

    if (userName && titleName) {
      return `${userName}'s review of ${titleName}`
    }
    if (titleName) {
      return `Review of ${titleName}`
    }
  }

  if (props.profile) {
    const displayName = props.profile.display_name || props.profile.username
    return `${displayName}'s profile`
  }

  if (props.list) {
    const creatorUsername = props.list.profiles?.username
    return creatorUsername
      ? `${props.list.title} by @${creatorUsername}`
      : props.list.title
  }

  if (props.title) {
    return (
      props.title.title ??
      props.title.name ??
      props.title.original_title ??
      props.title.original_name
    )
  }

  return 'Spectra'
})

</script>
